import React from 'react';
import { ChakraProvider, extendTheme } from '@chakra-ui/react';
import { theme as mythboundTheme } from '../config/theme';

// Extend Chakra UI theme with Mythbound custom theme
const theme = extendTheme({
  colors: {
    mythicBlue: mythboundTheme.colors.primary.main,
    darkAbyss: mythboundTheme.colors.primary.dark,
    arcaneGold: mythboundTheme.colors.primary.gold,
    mysticPurple: mythboundTheme.colors.secondary.purple,
    wizardWhite: mythboundTheme.colors.secondary.white,
    scrollParchment: mythboundTheme.colors.secondary.parchment,
    enchantedTeal: mythboundTheme.colors.accent.teal,
    runicSilver: mythboundTheme.colors.accent.silver,
    dragonScale: mythboundTheme.colors.accent.brown,
  },
  fonts: {
    heading: mythboundTheme.typography.fontFamily.heading,
    body: mythboundTheme.typography.fontFamily.body,
  },
  styles: {
    global: {
      body: {
        bg: mythboundTheme.colors.primary.dark,
        color: mythboundTheme.colors.secondary.white,
      },
    },
  },
  components: {
    Button: {
      baseStyle: {
        fontWeight: 'medium',
        borderRadius: 'md',
        _hover: {
          boxShadow: mythboundTheme.shadows.glow,
        },
      },
      variants: {
        primary: {
          bg: mythboundTheme.colors.primary.main,
          color: mythboundTheme.colors.primary.dark,
          _hover: {
            bg: mythboundTheme.colors.accent.teal,
            boxShadow: mythboundTheme.shadows.glow,
          },
        },
        secondary: {
          bg: 'transparent',
          border: '2px solid',
          borderColor: mythboundTheme.colors.primary.gold,
          color: mythboundTheme.colors.primary.gold,
          _hover: {
            bg: 'rgba(217, 181, 85, 0.1)',
            boxShadow: mythboundTheme.shadows.goldGlow,
          },
        },
        ghost: {
          color: mythboundTheme.colors.secondary.white,
          _hover: {
            bg: 'rgba(245, 245, 247, 0.1)',
          },
        },
      },
      defaultProps: {
        variant: 'primary',
      },
    },
    Card: {
      baseStyle: {
        container: {
          bg: mythboundTheme.colors.secondary.parchment,
          color: mythboundTheme.colors.accent.brown,
          borderRadius: 'md',
          boxShadow: mythboundTheme.shadows.md,
        },
      },
    },
    Modal: {
      baseStyle: {
        dialog: {
          bg: mythboundTheme.colors.primary.dark,
          borderWidth: '2px',
          borderColor: mythboundTheme.colors.primary.gold,
          borderRadius: 'md',
          boxShadow: mythboundTheme.shadows.goldGlow,
        },
        header: {
          fontFamily: mythboundTheme.typography.fontFamily.heading,
          color: mythboundTheme.colors.primary.gold,
        },
        body: {
          color: mythboundTheme.colors.secondary.white,
        },
        footer: {
          borderTopWidth: '1px',
          borderColor: mythboundTheme.colors.accent.silver,
        },
      },
    },
  },
});

/**
 * ThemeProvider component
 * 
 * Provides the Mythbound theme to the entire application using Chakra UI's ThemeProvider.
 * This component should wrap the entire application to ensure consistent styling.
 */
interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  return (
    <ChakraProvider theme={theme}>
      {children}
    </ChakraProvider>
  );
};

export default ThemeProvider;
